import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLogEntry } from '@/features/elva-work/store';
import type { HitlItem } from '../types';
import { MOCK_NOW } from '@/components/lists';
import { hasMoneyPermission, MONEY_APPROVAL_NOTE } from '@/features/shared/permissions';
import { answerItem, cantDo, checkIn, decideClaim } from '../store';

type PatientMode = '' | 'photo' | 'plan' | 'selfpay' | 'newins';

export function Spinner() {
  return (
    <span className="hl-spin" role="status" aria-live="polite">
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ animation: 'hl-rot 0.8s linear infinite' }}>
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
        <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/** The first stop outcome this user may choose (D245 hides the money ones for staff). */
function firstStop(item: HitlItem): string {
  const all = item.claimDecide?.stopReasons ?? [];
  const ok = hasMoneyPermission() ? all : all.filter((r) => r !== 'Write it off' && r !== 'The patient will pay it');
  return ok[0] ?? 'Something else';
}

interface Props {
  item: HitlItem;
  onBack?: () => void;
}

export function Detail({ item, onBack }: Props) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const doneRef = useRef<HTMLHeadingElement>(null);
  const [phase, setPhase] = useState<'form' | 'working'>('form');
  const [workLine, setWorkLine] = useState('');
  // answer state
  const [patientMode, setPatientMode] = useState<PatientMode>('');
  const [photo, setPhoto] = useState(false);
  const [plan, setPlan] = useState('');
  const [memberId, setMemberId] = useState(item.memberId ?? '');
  const [newInsPlan, setNewInsPlan] = useState('');
  const [newInsMemberId, setNewInsMemberId] = useState('');
  const [newInsStart, setNewInsStart] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [ref, setRef] = useState('');
  const [said, setSaid] = useState('');
  const [outcome, setOutcome] = useState('');
  const [outcomeDate, setOutcomeDate] = useState('');
  const [outcomeDetail, setOutcomeDetail] = useState('');
  const [money, setMoney] = useState('');
  const [moneyExtra, setMoneyExtra] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [doc, setDoc] = useState<Record<string, string>>({});
  // inline "Can't do this" (no modal — stays in card 01, §4/§6)
  const [cantOpen, setCantOpen] = useState(false);
  const [cantReason, setCantReason] = useState('');
  const [cantNote, setCantNote] = useState('');
  // claim "Stop and write it off" reason picker
  const [stopOpen, setStopOpen] = useState(false);
  const [stopReason, setStopReason] = useState(firstStop(item));

  const done = item.removed === 'done';

  useEffect(() => {
    headingRef.current?.focus();
    setPhase('form'); setWorkLine('');
    setPatientMode(''); setPhoto(false); setPlan(''); setMemberId(item.memberId ?? '');
    setNewInsPlan(''); setNewInsMemberId(''); setNewInsStart('');
    setPassword(''); setReason(''); setNote(''); setRef(''); setSaid('');
    setOutcome(''); setOutcomeDate(''); setOutcomeDetail('');
    setMoney(''); setMoneyExtra(''); setShowPw(false); setDoc({});
    setCantOpen(false); setCantReason(''); setCantNote('');
    setStopOpen(false); setStopReason(firstStop(item));
  }, [item.id, item.memberId, item.claimDecide]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (done) doneRef.current?.focus(); }, [done]);

  const docFilled = (item.docRead?.fields ?? []).every((f) => (doc[f.id] ?? '').trim() !== '');

  const chosenOutcome = item.callOutcomes?.find((o) => o.label === outcome);
  const outcomeFilled =
    outcome !== '' && (!chosenOutcome?.needsDate || outcomeDate !== '') && (!chosenOutcome?.needsDetail || outcomeDetail.trim() !== '');

  // G1: some choices need a date or a short text (HITL_TASK_CATALOG §3 G1).
  const moneyNeeds = item.moneyExtras?.[money];
  const todayIso = `${String(MOCK_NOW.getFullYear())}-${String(MOCK_NOW.getMonth() + 1).padStart(2, '0')}-${String(MOCK_NOW.getDate()).padStart(2, '0')}`;
  const moneyFilled = money !== '' && (!moneyNeeds || (moneyNeeds.kind === 'date' ? moneyExtra !== '' && moneyExtra <= todayIso : moneyExtra.trim() !== ''));

  const hasAnswer = (() => {
    switch (item.kind) {
      case 'patient':
        return (
          (patientMode === 'photo' && photo) ||
          (patientMode === 'plan' && plan !== '') ||
          patientMode === 'selfpay' ||
          (patientMode === 'newins' && newInsPlan !== '' && newInsMemberId.trim() !== '')
        );
      case 'access': return password.trim() !== '';
      case 'dentist': return reason !== '' && (reason !== 'Other reason' || note.trim() !== '');
      case 'call': return item.callOutcomes ? outcomeFilled : said.trim() !== '';
      case 'money': return moneyFilled;
      case 'document': return docFilled;
      default: return false;
    }
  })();

  const disabledReason = hasAnswer ? null : item.kind === 'access' ? 'Enter the new password first.' : item.kind === 'money' && money !== '' && moneyNeeds ? (moneyNeeds.kind === 'date' ? 'Enter the day you deposited it (not a future date).' : 'Say which account first.') : item.kind === 'document' ? 'Type both amounts first.' : item.kind === 'call' && item.callOutcomes ? 'Choose what happened first.' : 'Enter an answer first.';

  const save = async (): Promise<void> => {
    setPhase('working');
    for await (const ev of answerItem(item.id)) {
      if ('working' in ev) setWorkLine(ev.working);
      else if ('stillOpen' in ev) { setPhase('form'); return; }
      // done → store sets item.removed, re-render shows Done card
    }
  };

  const confirmCant = (): void => {
    const trimmed = cantNote.trim();
    cantDo(item.id, cantReason, cantReason === 'Something else' ? trimmed : undefined);
  };
  const cantNeedsNote = cantReason === 'Something else';
  const cantValid = cantReason !== '' && (!cantNeedsNote || cantNote.trim() !== '');

  // ── Done / closed panel ──
  if (done) {
    const answered = item.doneLabel?.startsWith('Answered');
    return (
      <main className="hl-detail">
        {onBack ? <button type="button" className="hl-back" onClick={onBack}>← All items</button> : null}
        <div className="hl-detail-inner">
          <div>
            <span className="hl-eyebrow">{item.kindLabel}</span>
            <h2 className="hl-title" tabIndex={-1} ref={headingRef}>{item.title}</h2>
          </div>
          <section className="hl-done">
            <h3 className="hl-done-title" tabIndex={-1} ref={doneRef}>
              <span className="hl-tick">{answered ? '✓' : '—'}</span> {answered ? 'Done' : 'Closed'}
            </h3>
            {answered || item.kind === 'claim'
              ? item.doneFindings?.map((f, i) => (
                  <div key={`f-${String(i)}`} className="hl-tickrow"><span className="hl-tick">✓</span><div>{f}</div></div>
                ))
              : <p style={{ margin: 0, color: 'var(--fg2)' }}>{item.cantDo.notice}</p>}
            <p className="hl-meta" style={{ marginTop: 10 }}>{item.doneLabel ?? ''} Moved to Done in the last 30 days.</p>
          </section>
          <p className="hl-history">History: {item.history.join(' · ')}</p>
        </div>
      </main>
    );
  }

  const isClaim = item.kind === 'claim';
  // D245: money actions are hidden, not refused, for a user without the permission.
  const canMoney = hasMoneyPermission();
  const stopOutcomes = (item.claimDecide?.stopReasons ?? []).filter((r) => canMoney || (r !== 'Write it off' && r !== 'The patient will pay it'));

  return (
    <main className="hl-detail">
      {onBack ? <button type="button" className="hl-back" onClick={onBack}>← All items</button> : null}
      <div className="hl-detail-inner">
        <div>
          <span className="hl-eyebrow">{item.kindLabel}</span>
          <h2 className="hl-title" tabIndex={-1} ref={headingRef}>{item.title}</h2>
          <p className="hl-meta">{item.metaLine}</p>
        </div>

        <section className="hl-card"><p style={{ margin: 0, color: 'var(--fg2)' }}>{item.why}</p></section>

        <section className="hl-card">
          <h3 className="hl-h3">What ELVA tried first</h3>
          {item.tries.slice(-4).map((t, i) => (
            <div key={`try-${String(i)}`} className="hl-try">
              <span className="tt">{t.time}</span>
              <span className={`ic ${t.ok ? 'ok' : 'x'}`}>{t.ok ? '✓' : '×'}</span>
              <span><span className="ch">{t.channel}.</span> {t.result}</span>
              {/* Each try opens ITS OWN log entry (rcm-04 Fix-on-staging). */}
              {getLogEntry(t.logEntryId) ? (
                <Link to={`/elva-work/log/${t.logEntryId}`} className="hl-src">Log</Link>
              ) : (
                <span />
              )}
            </div>
          ))}
        </section>

        {/* answer, by kind */}
        <section className="hl-card">
          {item.kind === 'patient' ? (
            <>
              <h3 className="hl-h3">Ask when the patient checks in</h3>
              <p className="hl-meta" style={{ marginBottom: 12 }}>Any one of these is enough. ELVA does the rest.</p>
              <div className="hl-radio-list" role="radiogroup" aria-label="How did she answer?">
                <label className="hl-radio"><input type="radio" name="hl-pmode" checked={patientMode === 'photo'} onChange={() => { setPatientMode('photo'); }} /><span>A photo of her card, both sides</span></label>
                <label className="hl-radio"><input type="radio" name="hl-pmode" checked={patientMode === 'plan'} onChange={() => { setPatientMode('plan'); }} /><span>Pick the plan</span></label>
                <label className="hl-radio"><input type="radio" name="hl-pmode" checked={patientMode === 'selfpay'} onChange={() => { setPatientMode('selfpay'); }} /><span>No insurance now — she’ll pay herself</span></label>
                <label className="hl-radio"><input type="radio" name="hl-pmode" checked={patientMode === 'newins'} onChange={() => { setPatientMode('newins'); }} /><span>New insurance</span></label>
              </div>

              {patientMode === 'photo' ? (
                <label className="hl-drop" style={{ display: 'block', cursor: 'pointer', marginTop: 12 }}>
                  {photo ? 'card.jpg · card-back.jpg' : <>Drop the photos here, or <span className="hl-src">choose files</span></>}
                  <input type="file" accept="image/*,.heic,.pdf" multiple className="hl-vh" onChange={(e) => { setPhoto((e.target.files?.length ?? 0) > 0); }} />
                </label>
              ) : null}

              {patientMode === 'plan' ? (
                <div style={{ marginTop: 12 }}>
                  <label className="hl-label" htmlFor="hl-plan">The plan</label>
                  <select id="hl-plan" className="hl-select" value={plan} onChange={(e) => { setPlan(e.target.value); }}>
                    <option value="">Choose the plan…</option>
                    {item.plans?.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <label className="hl-label" style={{ marginTop: 10 }} htmlFor="hl-member">Member ID, if it’s different</label>
                  <input id="hl-member" className="hl-field" value={memberId} onChange={(e) => { setMemberId(e.target.value); }} />
                </div>
              ) : null}

              {patientMode === 'selfpay' ? (
                <p className="hl-meta" style={{ marginTop: 12 }}>ELVA marks the visit self-pay in the PMS. No claim is sent; the claim will wait if she gets a plan later.</p>
              ) : null}

              {patientMode === 'newins' ? (
                <div style={{ marginTop: 12 }}>
                  <label className="hl-label" htmlFor="hl-newplan">The new plan</label>
                  <select id="hl-newplan" className="hl-select" value={newInsPlan} onChange={(e) => { setNewInsPlan(e.target.value); }}>
                    <option value="">Choose the plan…</option>
                    {item.plans?.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <label className="hl-label" style={{ marginTop: 10 }} htmlFor="hl-newmember">Member ID</label>
                  <input id="hl-newmember" className="hl-field" value={newInsMemberId} onChange={(e) => { setNewInsMemberId(e.target.value); }} />
                  <label className="hl-label" style={{ marginTop: 10 }} htmlFor="hl-newstart">Start date</label>
                  <input id="hl-newstart" className="hl-field" type="date" value={newInsStart} onChange={(e) => { setNewInsStart(e.target.value); }} />
                </div>
              ) : null}

              {item.checkInAvailable && item.group !== 'now' ? (
                <button type="button" className="hl-btn hl-btn-ghost" style={{ marginTop: 14 }} onClick={() => { checkIn(item.id); }}>
                  Patient checked in — move to Today
                </button>
              ) : null}
            </>
          ) : null}

          {item.kind === 'access' && item.passwordReset ? (
            <>
              <h3 className="hl-h3">Reset the password, then enter it</h3>
              {item.blocksLine ? <p className="hl-meta" style={{ marginBottom: 10, color: 'var(--warning, #8a5a00)' }}>{item.blocksLine}</p> : null}
              <ol className="hl-steps">
                <li>Open {item.resetUrl ?? 'the payer’s site'} and set a new password for {item.portal ?? 'the portal'}.</li>
                <li>Type the new password here.</li>
                <li>ELVA signs in to test it. No one needs to do anything else.</li>
              </ol>
              {item.accountUsername ? (
                <div style={{ marginBottom: 10 }}>
                  <label className="hl-label" htmlFor="hl-user">Account</label>
                  <input id="hl-user" className="hl-field mono" defaultValue={item.accountUsername} />
                </div>
              ) : null}
              <label className="hl-label" htmlFor="hl-pw">New password</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input id="hl-pw" className="hl-field mono" type={showPw ? 'text' : 'password'} autoComplete="off" spellCheck={false} value={password} onChange={(e) => { setPassword(e.target.value); }} />
                <button type="button" className="hl-btn hl-btn-ghost" aria-pressed={showPw} onClick={() => { setShowPw((v) => !v); }}>{showPw ? 'Hide' : 'Show'}</button>
              </div>
              <p className="hl-meta" style={{ marginTop: 6 }}>Saved encrypted. ELVA never shows it again and never logs it.</p>
            </>
          ) : null}

          {item.kind === 'dentist' ? (
            <>
              <h3 className="hl-h3">The reason, in the dentist’s words</h3>
              <div className="hl-radio-list" role="radiogroup" aria-label="The reason">
                {item.reasons?.map((r) => (
                  <label key={r} className="hl-radio"><input type="radio" name="hl-reason" checked={reason === r} onChange={() => { setReason(r); }} /><span>{r}</span></label>
                ))}
              </div>
              <textarea className="hl-textarea" style={{ marginTop: 10 }} placeholder="Or type what the dentist said" value={note} onChange={(e) => { setNote(e.target.value); if (e.target.value.trim()) setReason('Other reason'); }} />
            </>
          ) : null}

          {item.kind === 'call' && item.forCall ? (
            <>
              <h3 className="hl-h3">For the call</h3>
              <div className="hl-forcall">
                <div style={{ fontWeight: 600 }}>{item.forCall.number}. {item.forCall.ask}</div>
                <div className="hl-meta" style={{ marginTop: 6 }}>{item.forCall.facts.join(' · ')}</div>
              </div>

              {item.callOutcomes ? (
                <div style={{ marginTop: 14 }}>
                  <span id="hl-outcome-lbl" className="hl-label">What happened?</span>
                  <div className="hl-radio-list" role="radiogroup" aria-labelledby="hl-outcome-lbl">
                    {item.callOutcomes.map((o) => (
                      <label key={o.label} className="hl-radio">
                        <input type="radio" name="hl-outcome" checked={outcome === o.label} onChange={() => { setOutcome(o.label); setOutcomeDate(''); setOutcomeDetail(''); }} />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>
                  {chosenOutcome?.needsDate ? (
                    <div style={{ marginTop: 10 }}>
                      <label className="hl-label" htmlFor="hl-outcome-date">Date</label>
                      <input id="hl-outcome-date" className="hl-field" type="date" value={outcomeDate} onChange={(e) => { setOutcomeDate(e.target.value); }} />
                    </div>
                  ) : null}
                  {chosenOutcome?.needsDetail ? (
                    <div style={{ marginTop: 10 }}>
                      <label className="hl-label" htmlFor="hl-outcome-detail">{chosenOutcome.needsDetail}</label>
                      <input id="hl-outcome-detail" className="hl-field" value={outcomeDetail} onChange={(e) => { setOutcomeDetail(e.target.value); }} />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div style={{ marginTop: 12 }}>
                <label className="hl-label" htmlFor="hl-ref">Reference number they gave you (optional)</label>
                <input id="hl-ref" className="hl-field" value={ref} onChange={(e) => { setRef(e.target.value); }} />
              </div>
              <div style={{ marginTop: 12 }}>
                <label className="hl-label" htmlFor="hl-said">Notes{item.callOutcomes ? ' (optional)' : ''}</label>
                <textarea id="hl-said" className="hl-textarea" value={said} onChange={(e) => { setSaid(e.target.value); }} />
              </div>
            </>
          ) : null}

          {item.kind === 'money' ? (
            <>
              <h3 className="hl-h3">Do you have the check?</h3>
              <div className="hl-radio-list" role="radiogroup" aria-label="Do you have the check?">
                {item.moneyChoices?.map((c) => (
                  <label key={c} className="hl-radio"><input type="radio" name="hl-money" checked={money === c} onChange={() => { setMoney(c); setMoneyExtra(''); }} /><span>{c}</span></label>
                ))}
              </div>
              {moneyNeeds ? (
                <div style={{ marginTop: 10 }}>
                  <label className="hl-label" htmlFor="hl-money-extra">{moneyNeeds.label}</label>
                  <input id="hl-money-extra" className="hl-field" type={moneyNeeds.kind === 'date' ? 'date' : 'text'} {...(moneyNeeds.kind === 'date' ? { max: todayIso } : {})} value={moneyExtra} onChange={(e) => { setMoneyExtra(e.target.value); }} />
                </div>
              ) : null}
              {money !== '' && item.moneyNotes?.[money] ? (
                <p className="hl-meta" style={{ marginTop: 10 }}>{item.moneyNotes[money]}</p>
              ) : null}
            </>
          ) : null}

          {item.kind === 'document' && item.docRead ? (
            <>
              <h3 className="hl-h3">Read the amounts off the paper</h3>
              {item.docRead.fields.map((f) => (
                <div key={f.id} style={{ marginTop: 12 }}>
                  <label className="hl-label" htmlFor={`hl-doc-${f.id}`}>{f.label}</label>
                  <input id={`hl-doc-${f.id}`} className="hl-field mono" inputMode="decimal" placeholder="$0.00" value={doc[f.id] ?? ''} onChange={(e) => { setDoc((d) => ({ ...d, [f.id]: e.target.value })); }} />
                </div>
              ))}
              <p className="hl-meta" style={{ marginTop: 8 }}>{item.docRead.constraintText}</p>
            </>
          ) : null}

          {isClaim && item.claimDecide ? (
            <>
              <h3 className="hl-h3">Decide about this claim</h3>
              <p className="hl-meta" style={{ marginBottom: 10 }}>{item.claimDecide.amount} unpaid. Pick one.</p>
            </>
          ) : null}
        </section>

        {/* actions */}
        {phase === 'working' ? (
          <div className="hl-live" aria-live="polite"><Spinner /><span>{workLine || 'ELVA is working…'}</span></div>
        ) : isClaim && item.claimDecide ? (
          <div className="hl-card">
            <div className="hl-claim-choice">
              <button type="button" className="hl-btn hl-btn-primary hl-btn-lg" onClick={() => { decideClaim(item.id, 'keep'); }}>Keep going</button>
              <span className="hl-actionbar-meta">ELVA checks again on {item.claimDecide.dates.join(', ')}, then back here.</span>
            </div>
            <div className="hl-claim-choice" style={{ marginTop: 14 }}>
              {stopOpen ? (
                <div style={{ flex: '1 1 100%' }}>
                  <span className="hl-label">Why are you stopping?</span>
                  <div className="hl-radio-list" role="radiogroup" aria-label="Why are you stopping?">
                    {stopOutcomes.map((r) => (
                      <label key={r} className="hl-radio"><input type="radio" name="hl-stop" checked={stopReason === r} onChange={() => { setStopReason(r); }} /><span>{r}</span></label>
                    ))}
                  </div>
                  {stopReason === 'The patient will pay it' ? (
                    <p className="hl-meta" style={{ marginTop: 8 }}>{item.claimDecide.patientPayHelper}</p>
                  ) : null}
                  <div className="hl-actionbar" style={{ marginTop: 12 }}>
                    <button type="button" className="hl-btn hl-btn-primary" onClick={() => { decideClaim(item.id, 'stop', stopReason); }}>
                      {stopReason === 'Write it off' ? `Write off ${item.claimDecide.amount}` : 'Confirm'}
                    </button>
                    <button type="button" className="hl-btn hl-btn-ghost" onClick={() => { setStopOpen(false); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  {canMoney ? (
                    <>
                      <button type="button" className="hl-btn" onClick={() => { setStopOpen(true); }}>Write off {item.claimDecide.amount}</button>
                      <span className="hl-actionbar-meta">Or pick another outcome: the patient pays, billed elsewhere, something else.</span>
                    </>
                  ) : (
                    <>
                      <button type="button" className="hl-btn" onClick={() => { setStopOpen(true); }}>Stop following up</button>
                      <span className="hl-actionbar-meta">{MONEY_APPROVAL_NOTE} You can still stop it as billed to another insurance, or something else.</span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ) : cantOpen ? (
          <div className="hl-card">
            <span className="hl-label">{item.cantDo.title}</span>
            <div className="hl-radio-list" role="radiogroup" aria-label={item.cantDo.title}>
              {item.cantDo.reasons.map((r) => (
                <label key={r} className="hl-radio"><input type="radio" name="hl-cant" checked={cantReason === r} onChange={() => { setCantReason(r); }} /><span>{r}</span></label>
              ))}
            </div>
            {cantNeedsNote ? <input className="hl-field" style={{ marginTop: 10 }} placeholder="Tell ELVA what happened" value={cantNote} onChange={(e) => { setCantNote(e.target.value); }} /> : null}
            <p className="hl-meta" style={{ marginTop: 8 }}>{item.cantDo.notice}</p>
            <div className="hl-actionbar" style={{ marginTop: 12 }}>
              <button type="button" className="hl-btn hl-btn-primary" disabled={!cantValid} onClick={confirmCant}>Close this item</button>
              <button type="button" className="hl-btn hl-btn-ghost" onClick={() => { setCantOpen(false); }}>Back</button>
            </div>
          </div>
        ) : (
          <div className="hl-actionbar">
            <button type="button" className="hl-btn hl-btn-primary hl-btn-lg" disabled={Boolean(disabledReason)} aria-describedby={disabledReason ? 'hl-disabled' : undefined} onClick={() => { void save(); }}>{item.savePrimary}</button>
            <button type="button" className="hl-btn hl-btn-ghost" onClick={() => { setCantOpen(true); }}>I can’t do this</button>
            {disabledReason ? <span id="hl-disabled" className="hl-actionbar-meta">{disabledReason}</span> : <span className="hl-actionbar-meta">{item.nextMeta}</span>}
          </div>
        )}

        <p className="hl-history">History: {item.history.join(' · ')}</p>
      </div>
    </main>
  );
}
